import { Modal } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { cloneDeep, uniqueId } from "lodash-es";
import React from "react";
import * as Yup from "yup";
import { AskWhen } from "../../../../clients/graphqlTypes.js";
import {
  QuestionControlForm,
  QuestionControlSectionFormValues,
  useQuestionControlValidationSchema,
} from "../QuestionControlSection.js";

interface Props {
  values: QuestionControlSectionFormValues;
  promptPlaceholder?: string;
  allowedAskWhen?: AskWhen[];
  disabled?: boolean;
  hidePrompt?: boolean;
  handleClose: () => void;
  handleComplete: (
    values: QuestionControlSectionFormValues
  ) => void | Promise<void>;
}

export const QuestionControlModal = ({
  handleClose,
  handleComplete,
  values,
  promptPlaceholder,
  allowedAskWhen,
  disabled,
  hidePrompt,
}: Props) => {
  const { t } = useTranslate(["Global", "QuoteItem"]);

  const initialValues = React.useMemo(() => cloneDeep(values), [values]);
  const questionControlSchema = useQuestionControlValidationSchema();

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        ...questionControlSchema,
      }),
    [questionControlSchema]
  );

  const formId = React.useMemo(() => uniqueId(), []);

  const handleSubmit = async (values: QuestionControlSectionFormValues) => {
    await handleComplete(values);
    handleClose();
  };

  return (
    <Formik<QuestionControlSectionFormValues>
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={handleSubmit}
      initialValues={initialValues}
    >
      {formikProps => (
        <Modal
          title={t("Question control", { ns: "QuoteItem" })}
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Cancel", { ns: "Global" }),
              handleClick: handleClose,
              buttonProps: {
                variant: "text",
                disabled: formikProps.isSubmitting,
              },
            },
            {
              label: t("Save", { ns: "Global" }),
              buttonProps: {
                form: formId,
                type: "submit",
                loading: formikProps.isSubmitting,
                disabled: !formikProps.isValid || !formikProps.dirty,
              },
            },
          ]}
        >
          <Form id={formId}>
            <QuestionControlForm
              promptPlaceholder={promptPlaceholder}
              allowedAskWhen={allowedAskWhen}
              disabled={disabled}
              hidePrompt={hidePrompt}
            />
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
